import {
  type BoletaAnswers,
  type BoletaField,
  boletaFields,
  readBoleta,
  showBoletaErrors,
} from '../boleta.ts';
import { api, bytesText, download, sendFile } from '../console/api.ts';
import { type CarrierView, carrierName } from '../console/carriers.ts';
import { empty, field, meter, pageHead, pill, reportButtons } from '../console/components.ts';
import { ApiError, InputError } from '../console/errors.ts';
import { date, esc, icon, initials, PRODUCT, person, when } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { followsOnboarding, has, runsOnboarding } from '../console/session.ts';
import { $, act, textInput, toast } from '../console/ui.ts';
import {
  checkboxControl,
  checkedValues,
  dateControl,
  numberControl,
  radioControl,
  selectControl,
  textControl,
} from '../controls.ts';
import { fileButton } from '../file-picker.ts';
import { type BackgroundView, VERDICT, verdictPill } from './antecedentes.ts';
import {
  courseModuleHtml,
  type EnrollmentView,
  type ExamAttemptView,
  type ProgramView,
} from './cursos.ts';
import {
  candidatesTable,
  daysText,
  type FunnelView,
  fichaBlock,
  filtering,
  filtersBar,
  funnelCard,
  NO_FILTER,
  type OnboardingFilter,
  onboardingQuery,
  type RecertificationView,
  recertificationsCard,
} from './onboarding-tracking.ts';

/**
 * «Onboarding de agentes»: each candidate's path to agent (background check, selection,
 * psychometric evaluation, training and exam, proposal, dossier, RENAP, SIB, the carrier's code
 * and access to Nubi), with their boleta, their SIB credential and the training programs.
 */

type OnboardingView = {
  id: string;
  candidate: {
    fullName: string;
    email: string;
    phone: string | null;
    source: string;
    /** The privacy notice accepted on the boleta (who runs onboarding only). */
    privacy?: { notice: string; acceptedAt: string };
    /** The boleta's identity check (face vs DPI with RENAP), which replaces the signature. */
    biometricCheck?: {
      dpiLast4: string;
      reference: string;
      score: number;
      verifiedAt: string;
      consent: { text: string; version: string; acceptedAt: string };
    };
  };
  /** The biometrics step's outcome (its score only for who runs onboarding). */
  biometrics: { result: 'MATCH' | 'NO_MATCH'; at: string | null; score?: number } | null;
  /** When it opened, and the step it is in with its days (null: closed or no date). */
  openedAt: string | null;
  currentStep: { step: string; days: number | null; deadlineDays: number; overdue: boolean } | null;
  sibExpiresOn: string | null;
  renewing: string[];
  reactivation: { at: string; by: string; note: string; renewed: string[] } | null;
  carrier: string;
  productLines: string[];
  passMark: number | null;
  status: string;
  /** «Pendiente de verificación de antecedentes». */
  awaitingBackgroundCheck?: boolean;
  closedNote: string | null;
  /** The code the candidate sells with until the definitive one. */
  provisional?: { agentId: string; producerCode: string } | null;
  /** Who approved the definitive code: the direction and the management. */
  codeApprovals?: Partial<Record<'DIRECTION' | 'MANAGEMENT', { by: string; at: string }>>;
  appointment: {
    agentId: string;
    producerCode: string;
    productLines: string[];
    since: string;
    until: string | null;
    terms: {
      split?: string;
      commissions?: Record<string, string>;
      regime: string;
      nit?: string;
    } | null;
  } | null;
  done: number;
  total: number;
  available: string[];
  sibCredential: {
    number: string;
    status: 'DECLARED' | 'VALID' | 'INVALID';
    reason: string | null;
    declaredAt: string;
    reviewedAt: string | null;
    reviewedBy: string | null;
    evidence: {
      fileName: string;
      contentType: string;
      size: number;
      at: string;
      by: string;
    } | null;
  } | null;
  steps: {
    step: string;
    status: string;
    at: string | null;
    by: string | null;
    note: string | null;
    evidence: Record<string, string | number | boolean>;
    attempts?: number[];
    report?: { documentId: string; fileName: string; size: number; at: string } | null;
    invitations?: { url: string; channel: string; at: string; by: string }[];
    /** Days it took (done) or has taken so far (open), against its deadline. */
    days?: number | null;
    deadlineDays?: number | null;
    overdue?: boolean;
  }[];
  /** Only the version of the onboarding form it was filled with (answers: one at a time). */
  application: { formVersion: number } | null;
};
type OnboardingSettingsView = {
  psychometricTestUrl: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
};
/** The psychometric result, in the agency's words. */
const PSY_RESULT: Record<string, [string, string]> = {
  FIT: ['ok', 'Apta'],
  FIT_WITH_OBSERVATIONS: ['warn', 'Apta con observaciones'],
  UNFIT: ['bad', 'No apta'],
};
/** GET /v1/onboardings/:id/application: the form as filled, with the version's fields. */
type ApplicationView = {
  onboardingId: string;
  candidate: string;
  formVersion: number | null;
  currentVersion: number;
  fields: BoletaField[];
  answers: BoletaAnswers;
};
/** The onboarding forms opened in «Onboarding» (by onboarding id). */
const openApplications = new Map<string, ApplicationView>();
/** The list's search and filters, kept while the person stays on «Onboarding». */
let onbFilter: OnboardingFilter = { ...NO_FILTER };
const boletaPrefix = (id: string) => `bo-${id.replace(/[^A-Za-z0-9_-]/g, '')}`;
/** The dossier's onboarding form: its answers, editable, with the version it was filled with. */
function applicationCard(o: OnboardingView) {
  const a = openApplications.get(o.id);
  const version = o.application
    ? `Llenada con la versión ${o.application.formVersion} de la boleta`
    : 'Boleta sin llenar';
  if (!a)
    return `<div class="toolbar boleta-bar"><span class="muted small">${icon('lock')}${esc(version)} · datos personales</span><span class="spacer"></span>
      <button type="button" class="btn sm" data-onb-boleta="${esc(o.id)}">${o.application ? 'Ver boleta' : 'Llenar boleta'}</button></div>`;
  const prefix = boletaPrefix(o.id);
  const stale =
    a.formVersion !== null && a.formVersion !== a.currentVersion
      ? `<p class="note">${icon('alert')}<span>Esta boleta se llenó con la versión ${a.formVersion}; la agencia usa ahora la versión ${a.currentVersion}. Se corrige con los campos de la versión ${a.formVersion}, sin perder sus respuestas.</span></p>`
      : '';
  const open = o.status === 'IN_PROGRESS' || o.status === 'ACTIVE';
  return `<section class="boleta-panel" aria-label="Boleta de ${esc(o.candidate.fullName)}">
    <div class="toolbar boleta-bar"><strong>Boleta de onboarding</strong><span class="muted small">${a.formVersion === null ? `Se llenará con la versión ${a.currentVersion}` : `Versión ${a.formVersion}`}</span><span class="spacer"></span>
      <button type="button" class="btn sm" data-onb-boleta-close="${esc(o.id)}">Cerrar</button></div>
    ${stale}
    <form class="boleta boleta-form" data-boleta-form="${esc(o.id)}" novalidate>${boletaFields(a.fields, a.answers, prefix)}
      ${open ? `<div class="order-buttons"><button type="button" class="btn sm primary" data-onb-boleta-save="${esc(o.id)}">Guardar boleta</button><span class="muted small">Los documentos se anotan con la referencia del archivo recibido.</span></div>` : ''}</form>
  </section>`;
}
/** "8 de 12 preguntas al azar · 20 min" — how an exam is drawn and timed. */
const examRules = (x: ProgramView['exam']) =>
  `${x.questionsPerAttempt < x.bankSize ? `${x.questionsPerAttempt} preguntas al azar de ${x.bankSize}` : `${x.bankSize} preguntas`} · ${x.timeLimitMinutes ? `${x.timeLimitMinutes} min` : 'sin tiempo límite'}`;
/** The marked answers of a supervised exam form: question id → option as shown. */
const examAnswersOf = (form: HTMLFormElement | null) =>
  Object.fromEntries(
    [...(form?.querySelectorAll<HTMLInputElement>('wa-radio-group[data-question]') ?? [])]
      .filter((g) => g.value !== null && g.value !== '')
      .map((g) => [g.dataset.question ?? '', Number(g.value)]),
  );
/** "70 puntos (7 de 10 correctas, 8 respondidas)" — a graded attempt with its breakdown. */
const attemptText = (a: ExamAttemptView) =>
  a.questions === undefined
    ? `${a.score} puntos`
    : `${a.score} puntos (${a.right} de ${a.questions} correctas, ${a.answered} respondidas)`;
type EvField = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'check' | 'date';
  placeholder?: string;
};
/** Each step of the client's onboarding diagram, with the evidence it records. */
/** Steps a provider can run (RENAP, e-signature, SIB, member provisioning); manual stays as fallback. */
const ONB_PROVIDER: Record<string, { check: string; label: string; fields: EvField[] }> = {
  BIOMETRICS: {
    check: 'biometrics',
    label: 'Validar con RENAP',
    fields: [
      { key: 'nationalId', label: 'CUI del DPI', type: 'text', placeholder: '2451839200101' },
    ],
  },
  PROPOSAL_SIGNED: { check: 'signature', label: 'Enviar a firma electrónica', fields: [] },
  SIB_REGISTRATION: { check: 'regulator', label: 'Tramitar con la SIB', fields: [] },
  // After a refusal: the order for the definitive code again.
  CARRIER_ACTIVATION: {
    check: 'carrier-code',
    label: 'Pedir el código a la aseguradora',
    fields: [],
  },
  PLATFORM_ACCESS: {
    check: 'access',
    label: 'Dar acceso en Nubi',
    fields: [
      { key: 'email', label: 'Correo de acceso', type: 'text', placeholder: 'agente@agencia.com' },
    ],
  },
};
const ONB_STEPS: Record<string, { label: string; fields: EvField[]; action: string }> = {
  // Decided by the evaluator's verdict in «Antecedentes», never completed here.
  BACKGROUND_CHECK: { label: 'Verificación de antecedentes', action: '', fields: [] },
  SELECTION: {
    label: 'Selección y entrevistas',
    action: 'Aprobar selección',
    fields: [{ key: 'interviews', label: 'Entrevistas', type: 'number', placeholder: '2' }],
  },
  PSYCHOMETRIC_EVALUATION: {
    // Recorded with its own form (result, date, who applied it, report or external test).
    label: 'Evaluación psicométrica',
    action: 'Registrar resultado',
    fields: [],
  },
  TRAINING_PROGRAM: {
    label: 'Programa de capacitación aprobado',
    action: 'Aprobar programa',
    fields: [
      { key: 'program', label: 'Programa', type: 'text', placeholder: 'Vida y GM 2026' },
      { key: 'passMark', label: 'Nota mínima', type: 'number', placeholder: '70' },
    ],
  },
  TRAINING_EXAM: {
    label: 'Capacitación y examen final',
    action: 'Registrar intento',
    fields: [{ key: 'score', label: 'Puntaje', type: 'number', placeholder: '85' }],
  },
  CARRIER_KNOWLEDGE: {
    label: 'Información de la aseguradora (cerebro con IA)',
    // The API records the carrier's documents in «Conocimiento» as the evidence.
    action: 'Dar acceso al conocimiento',
    fields: [],
  },
  PROPOSAL_SIGNED: {
    label: 'Propuesta formal firmada y vinculación',
    action: 'Registrar firma',
    fields: [
      { key: 'signatureRef', label: 'Referencia de firma', type: 'text', placeholder: 'SIGN-0001' },
    ],
  },
  DOSSIER: {
    label: 'Expediente personal',
    action: 'Expediente completo',
    fields: [
      { key: 'dpi', label: 'DPI', type: 'check' },
      { key: 'nit', label: 'NIT/RTU', type: 'check' },
      { key: 'antecedentes', label: 'Antecedentes', type: 'check' },
      { key: 'bankAccount', label: 'Cuenta bancaria', type: 'check' },
    ],
  },
  BIOMETRICS: {
    label: 'Biométricos (RENAP)',
    action: 'Registrar validación',
    fields: [
      { key: 'renapRef', label: 'Referencia RENAP', type: 'text', placeholder: 'RENAP-OK-0000' },
    ],
  },
  SIB_REGISTRATION: {
    label: 'Registro en la SIB',
    action: 'Registrar número',
    fields: [
      { key: 'sibNumber', label: 'Número SIB', type: 'text', placeholder: 'SIB-AG-00000' },
      { key: 'expiresOn', label: 'Vence la autorización', type: 'date' },
    ],
  },
  CODE_APPROVAL: {
    label: 'Aprobación del código definitivo (dirección y gerencia)',
    action: 'Aprobar',
    fields: [],
  },
  CARRIER_ACTIVATION: {
    label: 'Código definitivo con la aseguradora',
    action: 'Registrar alta',
    fields: [
      { key: 'producerCode', label: 'Código de productor', type: 'text', placeholder: 'QZ-P-0000' },
      { key: 'agentId', label: 'Código de agente en Nubi', type: 'text', placeholder: 'marta' },
      { key: 'split', label: 'Comisión del agente (%)', type: 'number', placeholder: '70' },
      {
        key: 'regime',
        label: 'Régimen (SIMPLIFIED, PROFITS, SMALL_TAXPAYER)',
        type: 'text',
        placeholder: 'SIMPLIFIED',
      },
    ],
  },
  PLATFORM_ACCESS: {
    label: 'Alta de permisos en Nubi',
    action: 'Dar acceso a Nubi',
    fields: [
      { key: 'email', label: 'Correo de acceso', type: 'text', placeholder: 'agente@agencia.com' },
    ],
  },
};
/** Evidence keys every step may carry, in the words of the agency. */
const EVIDENCE_LABEL: Record<string, string> = {
  score: 'Puntaje',
  knowledgeDocuments: 'Documentos en «Conocimiento»',
  documents: 'Documentos',
  memberId: 'Usuario',
  filingRef: 'Orden de trabajo',
  sibNumber: 'Credencial SIB',
  assignedBy: 'Asignado por',
  verdict: 'Dictamen',
  proposalRef: 'Consulta de listas y buró',
  dpiLast4: 'DPI terminado en',
  via: 'Validado en',
  expiresOn: 'Vence',
};
const SIB_STATUS: Record<string, [string, string]> = {
  DECLARED: ['warn', 'Por revisar'],
  VALID: ['ok', 'Válida'],
  INVALID: ['bad', 'No válida'],
};

/**
 * The SIB credential the candidate already holds: its evidence and the administration's
 * review. Valid skips the training, the exam and the SIB filing (straight to the carrier).
 */
function sibBlock(o: OnboardingView) {
  const c = o.sibCredential;
  const manage = o.status === 'IN_PROGRESS' && has('agency_director', 'agency_admin');
  const sibDone = o.steps.find((x) => x.step === 'SIB_REGISTRATION')?.status === 'DONE';
  if (!c)
    return manage && !sibDone
      ? `<details class="sib-credential"><summary class="muted small">¿Ya tiene registro vigente ante la SIB?</summary>
        <div class="step-form provider">${field('Número de credencial SIB', textControl({ 'data-sib-number': o.id, maxlength: 30, autocomplete: 'off', placeholder: 'SIB-AG-40112' }))}
        <button type="button" class="btn sm" data-sib-declare="${esc(o.id)}">Registrar credencial</button></div></details>`
      : '';
  const [cls, label] = SIB_STATUS[c.status] ?? ['info', c.status];
  const input = `sib-file-${o.id}`;
  const explainStatus =
    c.status === 'VALID'
      ? `Validada ${c.reviewedAt ? `el ${esc(when(c.reviewedAt))} · ${esc(person(c.reviewedBy))}` : ''}. Omite la capacitación, el examen y el trámite con la SIB: sigue el alta con ${esc(carrierName(o.carrier))}.`
      : c.status === 'INVALID'
        ? `No válida: ${esc(c.reason ?? '')}. Sigue el programa de capacitación.`
        : 'Si la evidencia es válida, se omiten la capacitación, el examen y el trámite con la SIB.';
  const evidence = c.evidence
    ? `<button type="button" class="btn sm" data-sib-evidence="${esc(o.id)}">${icon('policy')}${esc(c.evidence.fileName)} · ${esc(bytesText(c.evidence.size))}</button><span class="small muted">Adjuntada por ${esc(person(c.evidence.by))}</span>`
    : '<span class="small muted">Sin evidencia todavía.</span>';
  const upload =
    manage && c.status !== 'VALID'
      ? fileButton({
          id: input,
          label: `${icon('upload')}${c.evidence ? 'Reemplazar evidencia' : 'Adjuntar evidencia'}`,
          accept: 'image/*,application/pdf',
          attrs: `data-sib-upload="${esc(o.id)}"`,
        })
      : '';
  const review =
    manage && c.status === 'DECLARED'
      ? `<div class="step-form provider">${field('Motivo si no es válida', textControl({ 'data-sib-reason': o.id, maxlength: 500, placeholder: 'La credencial está vencida' }))}
        <div class="order-buttons"><button type="button" class="btn sm primary" data-sib-valid="${esc(o.id)}" ${c.evidence ? '' : 'disabled title="Adjunta primero la evidencia"'}>Validar credencial</button>
        <button type="button" class="btn sm danger" data-sib-invalid="${esc(o.id)}">No es válida</button></div></div>`
      : '';
  return `<section class="sib-credential" aria-label="Credencial SIB de ${esc(o.candidate.fullName)}">
    <div class="step-head"><strong>Credencial SIB <span class="mono">${esc(c.number)}</span></strong>${pill(cls, label)}</div>
    <span class="small ${c.status === 'INVALID' ? 'fail' : 'muted'}">${explainStatus}</span>
    <div class="order-buttons">${evidence}${upload}<span class="muted small">PDF o foto, hasta 5 MB</span></div>${review}</section>`;
}

const ONB_STATUS: Record<string, [string, string]> = {
  IN_PROGRESS: ['info', 'En proceso'],
  ACTIVE: ['ok', 'Agente activo'],
  REJECTED: ['bad', 'Rechazado'],
  DEACTIVATED: ['CANCELLED', 'De baja'],
};

/**
 * The boleta's identity check, which replaced the candidate's signature: who RENAP matched, the
 * consent they accepted and when. The photos stay in the document storage.
 */
const identityNote = (o: OnboardingView) => {
  const c = o.candidate.biometricCheck;
  return c
    ? `<p class="note info">${icon('shield')}<span>Identidad confirmada con RENAP en la boleta: la cara coincide con el DPI terminado en <b class="mono">${esc(c.dpiLast4)}</b> (referencia <b class="mono">${esc(c.reference)}</b>, ${esc(when(c.verifiedAt))}). Sustituye la firma: aceptó «${esc(c.consent.text)}» <span class="muted">(${esc(c.consent.version)})</span>.</span></p>`
    : '';
};

/** What the psychometric step shows: result, date, who applied it, report and links sent. */
function psychometricEvidence(o: OnboardingView, st: OnboardingView['steps'][number]) {
  const e = st.evidence;
  const [cls, label] = PSY_RESULT[String(e.result ?? '')] ?? ['info', ''];
  const result = label
    ? `<span class="small">${pill(cls, label)} Aplicada el ${esc(date(String(e.takenOn ?? '')))} por <b>${esc(String(e.appliedBy ?? ''))}</b></span>`
    : '';
  const report = st.report
    ? `<span class="small">${icon('policy')} Informe <b>${esc(st.report.fileName)}</b> (${esc(bytesText(st.report.size))}) <button type="button" class="btn sm" data-psy-download="${esc(o.id)}">Descargar</button></span>`
    : '';
  const sent = (st.invitations ?? [])
    .map(
      (i) =>
        `<span class="small muted">${icon('inbox')} Prueba en línea enviada por ${i.channel === 'WHATSAPP' ? 'WhatsApp' : 'correo'} el ${esc(when(i.at))}</span>`,
    )
    .join('');
  return `${result}${report}${sent}`;
}

/** Recording the psychometric evaluation: send the external test, attach the report, result. */
function psychometricForm(o: OnboardingView, testUrl: string | null) {
  const id = esc(o.id);
  const send = testUrl
    ? `<button type="button" class="btn sm" data-psy-invite="${id}" data-send="EMAIL" title="${esc(o.candidate.email)}">Enviar prueba por correo</button>${o.candidate.phone ? `<button type="button" class="btn sm" data-psy-invite="${id}" data-send="WHATSAPP" title="${esc(o.candidate.phone)}">Por WhatsApp</button>` : ''}`
    : '<span class="muted small">Para enviar una prueba en línea, guarda su enlace en «Prueba psicométrica externa».</span>';
  return `<div class="step-form" data-psy-form="${id}">
    <div class="order-buttons">${send}${fileButton({ id: `psy-report-${o.id}`, label: `${icon('upload')}Subir informe (PDF)`, accept: 'application/pdf', attrs: `data-psy-report="${id}"` })}</div>
    ${field(
      'Resultado',
      selectControl({ 'data-psy': 'result' }, [
        { value: 'FIT', label: 'Apta' },
        { value: 'FIT_WITH_OBSERVATIONS', label: 'Apta con observaciones' },
        { value: 'UNFIT', label: 'No apta' },
      ]),
    )}
    ${field('Fecha de aplicación', dateControl({ 'data-psy': 'takenOn' }))}
    ${field('Aplicada por', textControl({ 'data-psy': 'appliedBy', maxlength: 120, placeholder: 'Licda. Ana Lucía Sic' }))}
    ${field('Observaciones o motivo', textControl({ 'data-psy': 'note', maxlength: 500, placeholder: 'Obligatorio si tiene observaciones o no es apta' }))}
    <div class="order-buttons"><button type="button" class="btn sm primary" data-psy-record="${id}">Registrar resultado</button>
    <span class="muted small">«No apta» cierra el onboarding y le avisa a la candidata por correo, sin el motivo.</span></div></div>`;
}

/** After a reactivation: what is still asked again before the agent sells. */
function renewingNote(o: OnboardingView) {
  const pending = o.renewing.filter((s) => o.steps.find((x) => x.step === s)?.status !== 'DONE');
  if (o.status !== 'IN_PROGRESS' || !o.reactivation || !pending.length) return '';
  return `<p class="note info">${icon('alert')}<span>Reactivado el ${esc(when(o.reactivation.at))}. Vende otra vez con el código <b class="mono">${esc(o.appointment?.agentId ?? '')}</b> cuando complete: ${pending.map((s) => esc(ONB_STEPS[s]?.label ?? s)).join(', ')}.</span></p>`;
}

/** When the SIB authorization expires, and the form to register it (who runs onboarding). */
function sibExpiryBlock(o: OnboardingView) {
  const registered =
    o.steps.find((x) => x.step === 'SIB_REGISTRATION')?.status === 'DONE' ||
    o.sibCredential?.status === 'VALID';
  if (!registered || (o.status !== 'ACTIVE' && o.status !== 'IN_PROGRESS')) return '';
  const shown = o.sibExpiresOn
    ? `<span class="small">${icon('calendar')} Autorización SIB vigente hasta el <b>${esc(date(o.sibExpiresOn))}</b></span>`
    : `<span class="small muted">${icon('calendar')} Sin vencimiento SIB registrado: Nubi no puede avisar antes de que venza.</span>`;
  const form = runsOnboarding()
    ? `${field(o.sibExpiresOn ? 'Nuevo vencimiento' : 'Vence la autorización', dateControl({ 'data-sib-expiry': o.id }))}<button type="button" class="btn sm" data-sib-expiry-save="${esc(o.id)}">Guardar vencimiento</button>`
    : '';
  return `<div class="toolbar order-actions onb-sib-expiry">${shown}<span class="spacer"></span>${form}</div>`;
}

/** The training certificate (PDF), once the exam was passed in a program of «Cursos». */
function certificateButton(o: OnboardingView) {
  const exam = o.steps.find((x) => x.step === 'TRAINING_EXAM');
  const program = o.steps.find((x) => x.step === 'TRAINING_PROGRAM');
  if (exam?.status !== 'DONE' || !program?.evidence.enrollmentId) return '';
  return `<div class="order-buttons onb-certificate"><button type="button" class="btn sm" data-onb-certificate="${esc(o.id)}">${icon('download')}Descargar constancia (PDF)</button><span class="muted small">Curso, nota, fecha y horas de la capacitación aprobada.</span></div>`;
}

/** Reactivar: the director brings a deactivated agent back, with a note. */
function reactivateForm(o: OnboardingView) {
  return `<div class="toolbar order-actions onb-reactivate">${field('Motivo de la reactivación', textControl({ 'data-onb-reactivate-note': o.id, maxlength: 500, placeholder: 'Regresa a la agencia' }))}<button type="button" class="btn sm primary" data-onb-reactivate="${esc(o.id)}">Reactivar agente</button>
    <span class="muted small">Conserva el código <b class="mono">${esc(o.appointment?.agentId ?? '')}</b>. Vuelve a pedir el acceso a Nubi y lo que se haya vencido (autorización SIB, capacitación).</span></div>`;
}

/** A step's name as the agency says it. */
const stepLabel = (step: string) => ONB_STEPS[step]?.label ?? step;

export async function onboarding() {
  // A screen opened anew (from the menu or another screen) starts without search or filters.
  if (!document.querySelector('#onb-search-form')) onbFilter = { ...NO_FILTER };
  const asked = onbFilter;
  const [list, carriers, programs, settings, funnel, recertifications] = await Promise.all([
    api<OnboardingView[]>('GET', `/v1/onboardings${onboardingQuery(asked)}`),
    api<(CarrierView & { productLines: string[] })[]>('GET', '/v1/carriers'),
    api<ProgramView[]>('GET', '/v1/training-programs').catch(() => [] as ProgramView[]),
    api<OnboardingSettingsView>('GET', '/v1/onboarding-settings'),
    // Sections of their own: if one fails, it says so with «Reintentar»; the rest still shows.
    api<FunnelView>('GET', '/v1/onboardings/funnel').catch(() => null),
    api<RecertificationView[]>('GET', '/v1/recertifications').catch(() => null),
  ]);
  const searched = filtering(asked);
  const testUrl = settings.psychometricTestUrl;
  // Training of onboardings whose program is assigned and exam still open.
  const enrollments = new Map<string, EnrollmentView>();
  await Promise.all(
    list
      .filter(
        (o) =>
          o.status === 'IN_PROGRESS' &&
          o.steps.find((x) => x.step === 'TRAINING_PROGRAM')?.status === 'DONE',
      )
      .map(async (o) => {
        const e = await api<EnrollmentView>(
          'GET',
          `/v1/onboardings/${encodeURIComponent(o.id)}/enrollment`,
        ).catch(() => null);
        if (e) enrollments.set(o.id, e);
      }),
  );
  const runs = runsOnboarding();
  let html = pageHead(
    'Onboarding de agentes',
    runs
      ? 'De candidato a agente de una aseguradora: antecedentes, selección, evaluación psicométrica, capacitación y examen, propuesta, expediente, RENAP, SIB, alta con la aseguradora por producto y alta en Nubi.'
      : 'El camino de cada candidato hasta el alta. La gerencia aprueba el código definitivo junto con la dirección; la administración lleva los pasos.',
    // The candidates of the last six months in Excel or PDF (#318).
    followsOnboarding() ? reportButtons('onboarding', 'el onboarding') : '',
  );
  const active = list.filter((o) => o.status === 'ACTIVE').length;
  const inProgress = list.filter((o) => o.status === 'IN_PROGRESS');
  const due = (recertifications ?? []).filter((r) => r.state !== 'UNDATED');
  html += `<section class="kpis" aria-label="Onboarding${searched ? ' (resultados de la búsqueda)' : ''}">
    <div class="card kpi"><span class="kpi-label">${icon('inbox')}En proceso</span><span class="kpi-value">${inProgress.length}</span><span class="kpi-foot">${inProgress.filter((o) => o.done >= 8).length} cerca del alta · ${inProgress.filter((o) => o.currentStep?.overdue).length} fuera de plazo</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('ok')}Agentes activos</span><span class="kpi-value">${active}</span><span class="kpi-foot">dados de alta desde Nubi</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('warn')}Cerrados</span><span class="kpi-value">${list.length - active - inProgress.length}</span><span class="kpi-foot">rechazados o de baja</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('calendar')}Por recertificar</span><span class="kpi-value">${recertifications ? due.length : '—'}</span><span class="kpi-foot">${due.filter((r) => r.state === 'EXPIRED').length} vencidas · aviso 30 días antes</span></div>
  </section>`;
  if (followsOnboarding())
    html += funnelCard(funnel, stepLabel) + recertificationsCard(recertifications);
  if (runs)
    html += `<form id="onb-form" class="card" novalidate><div class="card-head"><h2>Nuevo candidato</h2></div>
    <div class="form-grid">
      <fieldset><legend>Persona</legend>
        ${field('Nombre completo', textControl({ id: 'onb-name', maxlength: 120, placeholder: 'Marta Rojas' }))}
        ${field('Correo', textControl({ id: 'onb-email', type: 'email', placeholder: 'marta.rojas@correo.com' }))}
      </fieldset>
      <fieldset><legend>Contacto y origen</legend>
        ${field('Teléfono', textControl({ id: 'onb-phone', placeholder: '+502 5555 0199' }))}
        ${field(
          'Origen',
          selectControl(
            { id: 'onb-source' },
            ['soyagenteactualizado.com', 'Referido', 'Día de reclutamiento', 'Agencia'].map(
              (v) => ({ value: v, label: v }),
            ),
          ),
        )}
        ${field('Credencial SIB (si ya tiene registro)', textControl({ id: 'onb-sib', maxlength: 30, autocomplete: 'off', placeholder: 'SIB-AG-40112' }))}
      </fieldset>
      <fieldset><legend>Aseguradora y productos</legend>
        ${field(
          'Aseguradora',
          selectControl(
            { id: 'onb-carrier' },
            carriers.map((c) => ({ value: c.id, label: c.name })),
          ),
        )}
        <div class="field"><span>Líneas de producto</span><div id="onb-products" class="product-checks">${
          carriers[0]?.productLines
            .map((l) => checkboxControl({ class: 'check', value: l }, esc(PRODUCT[l] ?? l)))
            .join('') ?? ''
        }</div></div>
      </fieldset>
    </div>
    <div class="form-foot"><span class="muted small">Solo se ofrecen las líneas que la aseguradora vende; el alta habilita al agente para esas líneas.</span>
      <button class="btn primary" type="submit" id="onb-submit">Iniciar onboarding</button></div></form>`;
  if (runs)
    html += `<section class="card" aria-labelledby="psy-settings-title"><div class="card-head">${icon('target')}<h2 id="psy-settings-title">Prueba psicométrica externa</h2><span class="spacer"></span>${testUrl ? pill('ok', 'Configurada') : pill('CANCELLED', 'Sin configurar')}</div>
    <div class="toolbar order-actions">${field('Enlace de la prueba (https)', textControl({ id: 'psy-url', type: 'url', inputmode: 'url', maxlength: 500, placeholder: 'https://pruebas.ejemplo.com/agencia', value: testUrl ?? '' }))}
      <button type="button" class="btn sm primary" data-psy-save>Guardar enlace</button></div>
    <p class="muted small card-pad">Nubi envía este enlace a cada candidata en su paso de evaluación psicométrica. Déjalo vacío si la agencia aplica la evaluación en persona.${settings.updatedAt ? ` Actualizado el ${esc(when(settings.updatedAt))} por ${esc(person(settings.updatedBy))}.` : ''}</p></section>`;
  html += `<div class="section-head" id="onb-candidates"><h2>${searched ? 'Resultados' : 'Candidatos'}</h2><span class="muted small" aria-live="polite">${list.length} ${list.length === 1 ? 'expediente' : 'expedientes'}</span></div>${filtersBar(asked, carriers, runs)}`;
  if (!list.length)
    html += searched
      ? empty(
          'users',
          'Ningún candidato coincide',
          'Prueba con otro nombre, correo o DPI, o quita los filtros.',
          '<button type="button" class="btn" data-onb-clear>Ver todos</button>',
          2,
        )
      : empty(
          'inbox',
          'No hay candidatos todavía',
          runs
            ? 'Registra a la primera persona para seguir su camino hasta el alta como agente.'
            : 'La administración registra a los candidatos; aquí verás su avance y aprobarás su código.',
          '',
          2,
        );
  else html += candidatesTable(list, stepLabel, ONB_STATUS);
  /** The two approvals of the definitive code: who approved, and a button for whoever is missing. */
  const codeApprovals = (o: OnboardingView, can: boolean) => {
    const row = (role: 'DIRECTION' | 'MANAGEMENT', label: string, approvers: string[]) => {
      const a = o.codeApprovals?.[role];
      return `<li>${a ? pill('ok', 'Aprobado') : pill('CANCELLED', 'Pendiente')}<span>${label}${a ? ` · ${esc(person(a.by))} · ${esc(when(a.at))}` : ''}</span>${
        !a && can && has(...approvers)
          ? `<button type="button" class="btn sm primary" data-code-approve="${esc(o.id)}" data-as="${role}">Aprobar por la ${label.toLowerCase()}</button>`
          : ''
      }</li>`;
    };
    return `<ul class="approvals">${row('DIRECTION', 'Dirección', ['agency_director'])}${row('MANAGEMENT', 'Gerencia', ['agency_admin', 'agency_manager'])}</ul>
      <span class="muted small">Dos personas distintas. Con ambas aprobaciones se abre la orden a ${esc(carrierName(o.carrier))} para crear el código, con la comisión del agente por ramo.</span>`;
  };
  const stepRow = (o: OnboardingView, st: OnboardingView['steps'][number]) => {
    const ui = ONB_STEPS[st.step] ?? { label: st.step, fields: [], action: 'Completar' };
    const background = st.step === 'BACKGROUND_CHECK';
    const approval = st.step === 'CODE_APPROVAL';
    // The agency manager acts only on the code approval (ADR-0013); the rest is management's.
    const can =
      !background &&
      o.available.includes(st.step) &&
      (runsOnboarding() || (approval && has('agency_manager')));
    const exempt = st.evidence.exemption === 'SIB_CREDENTIAL';
    const ev = Object.entries(approval ? {} : st.evidence)
      // Internal references (the exemption marker, program and enrollment ids) stay off screen.
      .filter(([k]) => !['exemption', 'programId', 'enrollmentId'].includes(k))
      .map(
        ([k, v]) =>
          `${esc(ui.fields.find((f) => f.key === k)?.label ?? EVIDENCE_LABEL[k] ?? k)}: <b>${esc(k === 'verdict' ? (VERDICT[String(v)]?.[1] ?? String(v)) : k === 'expiresOn' ? date(String(v)) : v === true ? 'sí' : k === 'assignedBy' && v === 'NUBI' ? 'Nubi' : v === 'BOLETA' ? 'la boleta' : String(v))}</b>`,
      )
      .join(' · ');
    const psy = st.step === 'PSYCHOMETRIC_EVALUATION';
    const psyEv = psy ? psychometricEvidence(o, st) : '';
    const verdicts = background
      ? ((st as { assessments?: BackgroundView['backgroundCheck']['assessments'] }).assessments ??
        [])
      : [];
    const backgroundNote =
      background && st.status === 'PENDING' && o.status === 'IN_PROGRESS'
        ? `${verdicts
            .map(
              (a) =>
                `<span class="small">${verdictPill(a.verdict)} ${esc(when(a.at))} · ${esc(person(a.by))}${a.note ? ` · ${esc(a.note)}` : ''}</span>`,
            )
            .join(
              '',
            )}<span class="small muted">${icon('shield')} La evaluación emite el dictamen en «Antecedentes»; la dirección recibe el aviso.</span>`
        : '';
    const attempts = st.attempts?.length ? ` · intentos: ${st.attempts.join(', ')}` : '';
    const failures = (st as { failures?: { at: string; reason: string }[] }).failures ?? [];
    const tracking = (st as { tracking?: { at: string; reference: string; note: string } | null })
      .tracking;
    const prov = can && !tracking ? ONB_PROVIDER[st.step] : undefined;
    const fitting = programs.filter(
      (p) =>
        p.approved &&
        p.carrier === o.carrier &&
        o.productLines.every((l) => p.productLines.includes(l)),
    );
    const programForm =
      can && st.step === 'TRAINING_PROGRAM'
        ? fitting.length
          ? `<div class="step-form provider"><div class="field">${selectControl(
              { label: 'Programa aprobado', 'data-program-for': o.id },
              fitting.map((p) => ({
                value: p.id,
                label: `${p.title} · nota mínima ${p.exam.passMark}`,
              })),
            )}</div><button type="button" class="btn sm primary" data-assign-program="${esc(o.id)}">Asignar programa</button></div>`
          : `<p class="note">${icon('alert')}<span>No hay un programa aprobado de ${esc(carrierName(o.carrier))} que cubra ${o.productLines.map((l) => esc(PRODUCT[l] ?? l)).join(', ')}. Apruébalo abajo, en «Programas de capacitación».</span></p>`
        : '';
    // An enrollment already passed (a reactivated agent's old one) takes no new exam here: the
    // renewed exam is recorded by hand.
    const loaded = enrollments.get(o.id);
    const enr = loaded && !loaded.certificate ? loaded : undefined;
    const examForm =
      can && st.step === 'TRAINING_EXAM' && enr
        ? `<div class="training">${enr.course
            .map((m) =>
              m.lessons.length
                ? courseModuleHtml(m, {
                    complete: (l) =>
                      `<button type="button" class="btn sm primary" data-lesson="${esc(enr.id)}" data-lesson-id="${esc(l.id)}">Completar lección</button>`,
                    download: (mat) =>
                      `<button type="button" class="link" data-enr-material="${esc(enr.id)}" data-material-id="${esc(mat.id)}">${esc(mat.fileName)}</button>`,
                  })
                : `<div class="module ${m.done ? 'done' : ''}">${m.done ? icon('ok') : ''}<div><strong>${esc(m.title)}</strong><span class="muted small">${esc(m.summary)} · ${m.minutes} min</span></div>
              ${m.done ? '' : `<button type="button" class="btn sm" data-module="${esc(enr.id)}" data-module-id="${esc(m.id)}">Completar</button>`}</div>`,
            )
            .join('')}
          ${
            enr.modulesDone.length === enr.program.modules.length
              ? `<form class="exam" data-exam="${esc(enr.id)}" data-deadline="${esc(enr.exam?.deadline ?? '')}">${
                  enr.exam
                    ? `<p class="small muted">${icon('book')} Intento en curso · ${esc(examRules(enr.program.exam))}${enr.exam.deadline ? ` · termina el ${esc(when(enr.exam.deadline))}` : ''}. Cada respuesta se guarda al marcarla; al vencer el tiempo se califica lo respondido.</p>
                ${enr.exam.questions
                  .map((q, i) =>
                    radioControl(
                      {
                        label: `${i + 1}. ${q.text}`,
                        name: `${enr.id}-${q.id}`,
                        value:
                          enr.exam?.answers[q.id] === undefined
                            ? ''
                            : String(enr.exam.answers[q.id]),
                        'data-exam-answer': enr.id,
                        'data-question': q.id,
                      },
                      q.options.map((opt, k) => ({ value: String(k), labelHtml: esc(opt) })),
                    ),
                  )
                  .join('')}`
                    : `<p class="small muted">Examen supervisado: ${esc(examRules(enr.program.exam))}. Cada intento toma preguntas distintas y baraja las opciones.</p>`
                }
                <div class="order-buttons">${
                  enr.exam
                    ? `<button type="button" class="btn primary sm" data-grade="${esc(enr.id)}">Calificar examen</button>`
                    : enr.attemptsLeft
                      ? `<button type="button" class="btn primary sm" data-start-exam="${esc(enr.id)}">Iniciar examen supervisado</button>`
                      : ''
                }
                <button type="button" class="btn sm" data-exam-link="${esc(enr.id)}" data-send="EMAIL" title="${esc(o.candidate.email)}">Enviar examen por correo</button>
                ${o.candidate.phone ? `<button type="button" class="btn sm" data-exam-link="${esc(enr.id)}" data-send="WHATSAPP" title="${esc(o.candidate.phone)}">Por WhatsApp</button>` : ''}
                <button type="button" class="btn sm" data-exam-link="${esc(enr.id)}">Solo el enlace</button>
                <span class="muted small">Nota mínima ${enr.program.exam.passMark} · ${enr.attemptsLeft} intentos restantes${enr.attempts.length ? ` · último: ${esc(attemptText(enr.attempts.at(-1) as ExamAttemptView))}` : ''}</span></div></form>`
              : `<p class="muted small">El examen se habilita al completar los módulos y sus lecciones. Con «Enviar examen» la postulante también puede estudiar las lecciones desde su enlace.</p>
                <div class="order-buttons"><button type="button" class="btn sm" data-exam-link="${esc(enr.id)}" data-send="EMAIL" title="${esc(o.candidate.email)}">Enviar curso y examen por correo</button><button type="button" class="btn sm" data-exam-link="${esc(enr.id)}">Solo el enlace</button></div>`
          }</div>`
        : '';
    const provForm = prov
      ? `<div class="step-form provider" data-onb-prov-form="${esc(o.id)}:${st.step}">${prov.fields
          .map((f) =>
            field(
              esc(f.label),
              textControl({ 'data-pv': f.key, placeholder: f.placeholder ?? '' }),
            ),
          )
          .join(
            '',
          )}<button type="button" class="btn sm primary" data-onb-provider="${esc(o.id)}" data-check="${prov.check}">${esc(prov.label)}</button>
          <span class="muted small">o regístralo a mano:</span></div>`
      : '';
    const state =
      st.status === 'DONE'
        ? pill('ok', exempt ? 'Exento' : 'Hecho')
        : st.status === 'REJECTED'
          ? pill('bad', 'Rechazado')
          : tracking
            ? pill('WAITING_AGENCY', 'En trámite')
            : background && o.status === 'IN_PROGRESS'
              ? pill('warn', 'Por dictaminar')
              : can
                ? pill('info', 'Disponible')
                : pill('CANCELLED', 'Pendiente');
    const form = psy
      ? can
        ? psychometricForm(o, testUrl)
        : ''
      : can && !tracking && !approval
        ? `<div class="step-form" data-onb-form="${esc(o.id)}:${st.step}">${ui.fields
            .map((f) =>
              f.type === 'check'
                ? checkboxControl({ class: 'check small-check', 'data-ev': f.key }, esc(f.label))
                : field(
                    esc(f.label),
                    f.type === 'number'
                      ? numberControl({ 'data-ev': f.key, placeholder: f.placeholder ?? '' })
                      : f.type === 'date'
                        ? dateControl({ 'data-ev': f.key })
                        : textControl({ 'data-ev': f.key, placeholder: f.placeholder ?? '' }),
                  ),
            )
            .join('')}
        ${field('Nota', textControl({ 'data-note': true, placeholder: 'Opcional; obligatoria al rechazar' }))}
        <div class="order-buttons"><button type="button" class="btn sm primary" data-onb="${esc(o.id)}" data-step="${st.step}">${esc(ui.action)}</button>
        ${st.step === 'SELECTION' || st.step === 'TRAINING_EXAM' || st.step === 'SIB_REGISTRATION' ? `<button type="button" class="btn sm danger" data-onb-reject="${esc(o.id)}" data-step="${st.step}">Rechazar</button>` : ''}</div></div>`
        : '';
    const timing =
      st.days === null || st.days === undefined
        ? ''
        : `<span class="small ${st.overdue ? 'fail' : 'muted'}">${st.status === 'DONE' ? 'Tomó' : 'Lleva'} ${esc(daysText(st.days))} · plazo ${esc(daysText(st.deadlineDays))}${st.overdue ? ' · fuera de plazo' : ''}</span>`;
    return `<li class="step ${st.status.toLowerCase()}"><div class="step-head"><strong>${esc(ui.label)}</strong>${state}</div>${timing}
      ${st.at ? `<span class="muted small">${esc(when(st.at))} · ${esc(person(st.by))}${attempts}</span>` : attempts ? `<span class="muted small">${attempts.slice(3)}</span>` : ''}
      ${psy ? psyEv : ev ? `<span class="small">${ev}</span>` : ''}${st.note ? `<span class="timeline-note">${esc(st.note)}</span>` : ''}
      ${tracking && st.status !== 'DONE' ? `<span class="small muted">${icon('inbox')} ${esc(tracking.note)} · orden <b class="mono">${esc(tracking.reference)}</b> desde el ${esc(when(tracking.at))}. ${st.step === 'CARRIER_ACTIVATION' ? 'Se completa al registrar en «Órdenes» el código y la comisión por ramo.' : 'Se completa al registrar el número en «Órdenes».'}</span>` : ''}${approval && st.status !== 'DONE' && (can || Object.keys(o.codeApprovals ?? {}).length) ? codeApprovals(o, can) : ''}${approval && st.status === 'DONE' ? `<span class="small">Dirección: <b>${esc(person(o.codeApprovals?.DIRECTION?.by ?? null))}</b> · Gerencia: <b>${esc(person(o.codeApprovals?.MANAGEMENT?.by ?? null))}</b></span>` : ''}${failures.map((f) => `<span class="small fail">${icon('alert')} ${esc(when(f.at))} · ${esc(f.reason)}</span>`).join('')}${backgroundNote}${programForm}${examForm}${provForm}${st.step === 'TRAINING_EXAM' && enr ? '' : form}</li>`;
  };
  const cards = list
    .map((o) => {
      const [cls, label] = ONB_STATUS[o.status] ?? ['info', o.status];
      return `<article class="card"><div class="card-head">
        <span class="avatar onb-avatar" aria-hidden="true">${esc(initials(o.candidate.fullName))}</span>
        <div class="policy-title"><strong>${esc(o.candidate.fullName)}</strong>
          <div class="policy-meta"><span>${esc(carrierName(o.carrier))}</span><span>${o.productLines.map((l) => esc(PRODUCT[l] ?? l)).join(', ')}</span><span>${esc(o.candidate.source)}</span>${o.appointment ? `<span>Agente <b class="mono">${esc(o.appointment.agentId)}</b> · código <b class="mono">${esc(o.appointment.producerCode)}</b></span>` : o.provisional && o.status === 'IN_PROGRESS' ? `<span>Código provisional <b class="mono">${esc(o.provisional.agentId)}</b> · vende con <b class="mono">${esc(o.provisional.producerCode)}</b></span>` : ''}</div></div>
        <span class="spacer"></span>
        <span class="progress-text">${meter(o.done / o.total, 'inline')}${o.done} de ${o.total} pasos</span>${o.awaitingBackgroundCheck ? pill('WAITING_AGENCY', 'Pendiente de verificación de antecedentes') : pill(cls, label)}</div>
        ${
          o.currentStep
            ? `<p class="onb-current small" data-onb-current="${esc(o.id)}">${icon('clock')}<span>Paso actual: <b>${esc(stepLabel(o.currentStep.step))}</b> · ${esc(daysText(o.currentStep.days))} en el paso (plazo ${esc(daysText(o.currentStep.deadlineDays))})</span>${o.currentStep.overdue ? pill('bad', 'Fuera de plazo') : ''}</p>`
            : ''
        }
        ${runs ? identityNote(o) : ''}
        ${fichaBlock(o, runs)}
        ${renewingNote(o)}
        ${o.closedNote ? `<p class="note ${o.status === 'REJECTED' ? 'bad' : ''}">${icon('alert')}<span>${esc(o.closedNote)}</span></p>` : ''}
        ${sibBlock(o)}
        ${runs ? applicationCard(o) : ''}
        <details ${o.status === 'IN_PROGRESS' ? 'open' : ''}><summary class="muted small">Pasos del onboarding</summary><ol class="steps">${o.steps.map((st) => stepRow(o, st)).join('')}</ol></details>
        ${sibExpiryBlock(o)}
        ${certificateButton(o)}
        ${o.status === 'ACTIVE' && has('agency_director') ? `<div class="toolbar order-actions">${field('Motivo de la baja', textControl({ 'data-onb-note': o.id, placeholder: 'Renuncia' }))}<button type="button" class="btn sm danger" data-onb-deactivate="${esc(o.id)}">Dar de baja</button></div>` : ''}
        ${o.status === 'DEACTIVATED' && o.appointment && has('agency_director') ? reactivateForm(o) : ''}
      </article>`;
    })
    .join('');
  const programsHtml = programs.length
    ? `<section class="card"><div class="card-head"><h2>Programas de capacitación</h2><span class="spacer"></span><span class="muted small">La dirección aprueba cada programa antes de asignarlo. Sus lecciones y materiales se arman en «Cursos».</span></div>
      <div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Programa</th><th>Aseguradora</th><th>Productos</th><th class="num">Módulos</th><th>Examen</th><th class="num">Nota mínima</th><th>Estado</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
      ${programs
        .map(
          (
            p,
          ) => `<tr><td><strong>${esc(p.title)}</strong></td><td>${esc(carrierName(p.carrier))}</td><td>${p.productLines.map((l) => esc(PRODUCT[l] ?? l)).join(', ')}</td>
          <td class="num">${p.modules.length}</td><td>${esc(examRules(p.exam))} · ${p.exam.maxAttempts} intentos</td><td class="num">${p.exam.passMark}</td>
          <td>${p.approved ? pill('ok', 'Aprobado') : pill('warn', 'Borrador')}</td>
          <td class="actions">${!p.approved && has('agency_director') ? `<button type="button" class="btn sm primary" data-approve-program="${esc(p.id)}">Aprobar</button>` : ''}</td></tr>`,
        )
        .join('')}</tbody></table></div></section>`
    : '';
  return `${html}${list.length ? `<section class="policies">${cards}</section>` : ''}${programsHtml}`;
}

/** The dossiers' boletas opened on screen are closed when the session changes. */
export function resetOnboarding() {
  openApplications.clear();
  onbFilter = { ...NO_FILTER };
}

/** Reads the search and filters and paints the list they answer. */
function searchOnboardings(focus: string) {
  const value = (id: string) =>
    (document.querySelector<HTMLInputElement>(`#${id}`)?.value ?? '').trim();
  onbFilter = {
    q: value('onb-q'),
    status: value('onb-f-status'),
    carrier: value('onb-f-carrier'),
    productLine: value('onb-f-line'),
    agent: value('onb-f-agent'),
    olderThanDays: value('onb-f-age'),
  };
  void render(current, { quiet: true }).then(() => $(`#${focus}`)?.focus());
}

export const actions: Actions = {
  click: {
    onbBoleta: (t, d) => {
      if (!d.onbBoleta) return;
      const id = d.onbBoleta;
      void act(t, 'Abriendo la boleta…', async () => {
        openApplications.set(
          id,
          await api<ApplicationView>(
            'GET',
            `/v1/onboardings/${encodeURIComponent(id)}/application`,
          ),
        );
        await render(current, { quiet: true });
        $(
          `[data-boleta-form="${CSS.escape(id)}"] input, [data-boleta-form="${CSS.escape(id)}"] select`,
        )?.focus();
        return 'Boleta abierta.';
      });
    },
    onbBoletaClose: (_, d) => {
      if (!d.onbBoletaClose) return;
      openApplications.delete(d.onbBoletaClose);
      void render(current, { quiet: true });
    },
    onbBoletaSave: (t, d) => {
      if (!d.onbBoletaSave) return;
      const id = d.onbBoletaSave;
      const a = openApplications.get(id);
      const form = $(`[data-boleta-form="${CSS.escape(id)}"]`);
      if (!a || !form) return;
      void act(t, 'Guardando la boleta…', async () => {
        showBoletaErrors(form, boletaPrefix(id), []);
        try {
          const saved = await api<ApplicationView>(
            'PUT',
            `/v1/onboardings/${encodeURIComponent(id)}/application`,
            { answers: readBoleta(form, a.fields) },
          );
          openApplications.set(id, saved);
        } catch (e) {
          if (e instanceof ApiError && e.errors.length)
            showBoletaErrors(form, boletaPrefix(id), e.errors);
          throw e;
        }
        await render(current, { quiet: true });
        return `Boleta de ${openApplications.get(id)?.candidate ?? 'la candidata'} guardada.`;
      });
    },
    onb: (t, d) => {
      if (!d.onb || !d.step) return;
      const box = document.querySelector<HTMLElement>(
        `[data-onb-form="${CSS.escape(`${d.onb}:${d.step}`)}"]`,
      );
      const evidence: Record<string, string | number | boolean> = {};
      for (const input of box?.querySelectorAll<HTMLInputElement>('[data-ev]') ?? []) {
        const k = input.dataset.ev as string;
        if (input.matches('wa-checkbox')) evidence[k] = input.checked;
        else if (input.value.trim())
          evidence[k] = input.matches('wa-number-input') ? Number(input.value) : input.value.trim();
      }
      const step = d.step;
      const note = box?.querySelector<HTMLInputElement>('[data-note]')?.value.trim();
      const regime = String(evidence.regime ?? 'SIMPLIFIED').toUpperCase();
      const body =
        step === 'TRAINING_EXAM'
          ? { score: Number(evidence.score) }
          : step === 'CARRIER_ACTIVATION'
            ? {
                producerCode: String(evidence.producerCode ?? ''),
                agentId: String(evidence.agentId ?? ''),
                split: String(evidence.split ?? '70'),
                regime,
              }
            : { evidence, ...(note ? { note } : {}) };
      void act(t, 'Registrando el paso…', async () => {
        if (step === 'TRAINING_EXAM' && !(Number(evidence.score) >= 0))
          throw new InputError('Escribe el puntaje del examen.');
        const o = await api<OnboardingView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(d.onb ?? '')}/steps/${step}`,
          body,
        );
        await render(current, { quiet: true });
        const st = o.steps.find((x) => x.step === step);
        if (step === 'TRAINING_EXAM' && st?.status !== 'DONE')
          return `Intento registrado (${evidence.score} puntos); la nota mínima es ${o.passMark}.`;
        return o.status === 'ACTIVE'
          ? `${o.candidate.fullName} ya es agente activo en Nubi.`
          : `«${ONB_STEPS[step]?.label ?? step}» completado (${o.done} de ${o.total}).`;
      });
    },
    onbReject: (t, d) => {
      if (!d.onbReject || !d.step) return;
      const box = document.querySelector<HTMLElement>(
        `[data-onb-form="${CSS.escape(`${d.onbReject}:${d.step}`)}"]`,
      );
      void act(t, 'Registrando el rechazo…', async () => {
        const note = box?.querySelector<HTMLInputElement>('[data-note]')?.value.trim();
        if (!note) throw new InputError('Escribe el motivo del rechazo.');
        await api('POST', `/v1/onboardings/${encodeURIComponent(d.onbReject ?? '')}/reject`, {
          step: d.step,
          note,
        });
        await render(current, { quiet: true });
        return 'Expediente cerrado como rechazado.';
      });
    },
    onbDeactivate: (t, d) => {
      if (!d.onbDeactivate) return;
      const note = document
        .querySelector<HTMLInputElement>(`[data-onb-note="${CSS.escape(d.onbDeactivate)}"]`)
        ?.value.trim();
      void act(t, 'Dando de baja…', async () => {
        if (!note) throw new InputError('Escribe el motivo de la baja.');
        await api(
          'POST',
          `/v1/onboardings/${encodeURIComponent(d.onbDeactivate ?? '')}/deactivate`,
          { note },
        );
        await render(current, { quiet: true });
        return 'Agente dado de baja; ya no vende con la aseguradora.';
      });
    },
    onbClear: () => {
      onbFilter = { ...NO_FILTER };
      void render(current, { quiet: true }).then(() => $('#onb-q')?.focus());
    },
    onbReactivate: (t, d) => {
      if (!d.onbReactivate) return;
      const id = d.onbReactivate;
      const note = document
        .querySelector<HTMLInputElement>(`[data-onb-reactivate-note="${CSS.escape(id)}"]`)
        ?.value.trim();
      void act(t, 'Reactivando al agente…', async () => {
        if (!note || note.length < 3) throw new InputError('Escribe el motivo de la reactivación.');
        const o = await api<OnboardingView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(id)}/reactivate`,
          { note },
        );
        await render(current, { quiet: true });
        const asked = o.renewing.map((s) => ONB_STEPS[s]?.label ?? s).join(', ');
        return `${o.candidate.fullName} reactivado. Vende otra vez al completar: ${asked}.`;
      });
    },
    sibExpirySave: (t, d) => {
      if (!d.sibExpirySave) return;
      const id = d.sibExpirySave;
      const expiresOn = document
        .querySelector<HTMLInputElement>(`[data-sib-expiry="${CSS.escape(id)}"]`)
        ?.value.trim();
      void act(t, 'Guardando el vencimiento…', async () => {
        if (!expiresOn) throw new InputError('Elige la fecha en que vence la autorización SIB.');
        const o = await api<OnboardingView>(
          'PUT',
          `/v1/onboardings/${encodeURIComponent(id)}/sib-expiry`,
          { expiresOn },
        );
        await render(current, { quiet: true });
        return `Autorización SIB de ${o.candidate.fullName} vigente hasta el ${date(o.sibExpiresOn)}.`;
      });
    },
    onbCertificate: (t, d) => {
      if (!d.onbCertificate) return;
      const id = d.onbCertificate;
      void act(t, 'Generando la constancia…', async () => {
        const name = await download(
          `/v1/onboardings/${encodeURIComponent(id)}/certificate`,
          'constancia.pdf',
        );
        return `«${name}» descargada.`;
      });
    },
    onbProvider: (t, d) => {
      if (!d.onbProvider || !d.check) return;
      const onb = d.onbProvider;
      const check = d.check;
      const box = t.closest<HTMLElement>('[data-onb-prov-form]');
      const nationalId = box
        ?.querySelector<HTMLInputElement>('[data-pv="nationalId"]')
        ?.value.replace(/\D/g, '');
      const email = box?.querySelector<HTMLInputElement>('[data-pv="email"]')?.value.trim();
      const busy: Record<string, string> = {
        biometrics: 'Validando con RENAP…',
        signature: 'Enviando la propuesta a firma…',
        regulator: 'Tramitando con la SIB…',
        access: 'Creando su usuario en Nubi…',
      };
      void act(t, busy[check] ?? 'Consultando…', async () => {
        if (check === 'biometrics' && !nationalId)
          throw new InputError('Escribe el CUI del DPI (13 dígitos).');
        if (check === 'access' && !email)
          throw new InputError('Escribe el correo con el que entrará.');
        const o = await api<OnboardingView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(onb)}/providers/${check}`,
          {
            ...(nationalId ? { nationalId } : {}),
            ...(email ? { email } : {}),
          },
        );
        await render(current, { quiet: true });
        const stepKey = Object.entries(ONB_PROVIDER).find(([, v]) => v.check === check)?.[0] ?? '';
        const st = o.steps.find((x) => x.step === stepKey) as
          | {
              status: string;
              failures?: { reason: string }[];
              tracking?: { reference: string } | null;
            }
          | undefined;
        if (st?.status !== 'DONE' && st?.tracking)
          return `Se abrió la orden ${st.tracking.reference} a ${carrierName(o.carrier)} para el registro ante la SIB; síguela en «Órdenes».`;
        if (st?.status !== 'DONE')
          throw new InputError(st?.failures?.at(-1)?.reason ?? 'El proveedor no confirmó el paso.');
        return o.status === 'ACTIVE'
          ? `${o.candidate.fullName} ya es agente activo en Nubi.`
          : `«${ONB_STEPS[stepKey]?.label ?? stepKey}» confirmado por el proveedor (${o.done} de ${o.total}).`;
      });
    },
    codeApprove: (t, d) => {
      if (!d.codeApprove || !d.as) return;
      const who = d.as === 'DIRECTION' ? 'la dirección' : 'la gerencia';
      void act(t, 'Aprobando el código…', async () => {
        const o = await api<OnboardingView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(d.codeApprove ?? '')}/code-approvals`,
          { as: d.as },
        );
        await render(current, { quiet: true });
        const order = o.steps.find((x) => x.step === 'CARRIER_ACTIVATION') as
          | { tracking?: { reference: string } | null }
          | undefined;
        return order?.tracking
          ? `Código aprobado por ${who}. Se abrió la orden ${order.tracking.reference} a ${carrierName(o.carrier)} para crearlo.`
          : `Código aprobado por ${who}. Falta la otra aprobación.`;
      });
    },
    assignProgram: (t, d) => {
      if (!d.assignProgram) return;
      const programId = document.querySelector<HTMLSelectElement>(
        `[data-program-for="${CSS.escape(d.assignProgram)}"]`,
      )?.value;
      void act(t, 'Asignando el programa…', async () => {
        const e = await api<EnrollmentView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(d.assignProgram ?? '')}/enrollment`,
          { programId },
        );
        await render(current, { quiet: true });
        return `Programa «${e.program.title}» asignado: ${e.program.modules.length} módulos.`;
      });
    },
    module: (t, d) => {
      if (!d.module || !d.moduleId) return;
      void act(t, 'Registrando el módulo…', async () => {
        await api(
          'POST',
          `/v1/enrollments/${encodeURIComponent(d.module ?? '')}/modules/${encodeURIComponent(d.moduleId ?? '')}`,
          {},
        );
        await render(current, { quiet: true });
        return 'Módulo completado.';
      });
    },
    lesson: (t, d) => {
      if (!d.lesson || !d.lessonId) return;
      void act(t, 'Registrando la lección…', async () => {
        await api(
          'POST',
          `/v1/enrollments/${encodeURIComponent(d.lesson ?? '')}/lessons/${encodeURIComponent(d.lessonId ?? '')}`,
          {},
        );
        await render(current, { quiet: true });
        return 'Lección completada; se desbloqueó la siguiente.';
      });
    },
    enrMaterial: (t, d) => {
      if (!d.enrMaterial || !d.materialId) return;
      const path = `/v1/enrollments/${encodeURIComponent(d.enrMaterial)}/materials/${encodeURIComponent(d.materialId)}/content`;
      void act(t, 'Descargando…', async () => `«${await download(path)}» descargado.`);
    },
    examLink: (t, d) => {
      if (!d.examLink) return;
      const id = d.examLink;
      const send = d.send;
      void act(t, send ? 'Enviando el examen…' : 'Generando el enlace…', async () => {
        const r = await api<{
          path: string;
          expiresAt: string;
          delivery: { channel: string; to: string; status: string; error: string | null } | null;
        }>('POST', `/v1/enrollments/${encodeURIComponent(id)}/exam-link`, send ? { send } : {});
        const url = `${location.origin}${r.path}`;
        const row = t.closest('.order-buttons');
        row?.parentElement?.querySelector('.exam-link')?.remove();
        row?.insertAdjacentHTML(
          'afterend',
          `<div class="note exam-link">${icon('lock')}<div class="link-box"><span class="small">Envíalo solo al candidato. Vence el ${esc(when(r.expiresAt))}, sirve para un envío y deja sin efecto cualquier enlace anterior.</span>
        <div class="link-row">${textControl({ class: 'mono', readonly: true, label: 'Enlace del examen', hideLabel: true, value: url })}<button type="button" class="btn sm" data-copy-link>Copiar</button></div></div></div>`,
        );
        if (r.delivery?.status === 'FAILED')
          throw new InputError(
            `No se pudo enviar a ${r.delivery.to}: ${r.delivery.error ?? 'el proveedor falló'}. Copia el enlace y envíalo tú.`,
          );
        return r.delivery
          ? `Examen enviado por ${r.delivery.channel === 'EMAIL' ? 'correo' : 'WhatsApp'} a ${r.delivery.to}.`
          : 'Enlace generado.';
      });
    },
    copyLink: (t) => {
      const input = t.parentElement?.querySelector<HTMLInputElement>('wa-input');
      if (!input) return;
      input.select();
      void navigator.clipboard?.writeText(input.value).then(
        () => toast('Enlace copiado.'),
        () => toast('Selecciona el enlace y cópialo.'),
      );
    },
    startExam: (t, d) => {
      if (!d.startExam) return;
      const id = d.startExam;
      void act(t, 'Sorteando las preguntas…', async () => {
        const r = await api<EnrollmentView & { graded: ExamAttemptView | null }>(
          'POST',
          `/v1/enrollments/${encodeURIComponent(id)}/exam/start`,
          {},
        );
        await render(current, { quiet: true });
        if (r.graded)
          throw new InputError(`Se acabó el tiempo del intento: ${attemptText(r.graded)}.`);
        return `Examen iniciado: ${r.exam?.questions.length ?? 0} preguntas.`;
      });
    },
    grade: (t, d) => {
      if (!d.grade) return;
      const form = document.querySelector<HTMLFormElement>(`[data-exam="${CSS.escape(d.grade)}"]`);
      const answers = examAnswersOf(form);
      const total = form?.querySelectorAll('wa-radio-group[data-question]').length ?? 0;
      const deadline = form?.dataset.deadline;
      void act(t, 'Calificando el examen…', async () => {
        const timeUp = !!deadline && Date.now() > Date.parse(deadline);
        if (!timeUp && Object.keys(answers).length < total)
          throw new InputError('Responde todas las preguntas.');
        const r = await api<EnrollmentView & { attempt: ExamAttemptView }>(
          'POST',
          `/v1/enrollments/${encodeURIComponent(d.grade ?? '')}/exam`,
          { answers },
        );
        await render(current, { quiet: true });
        if (!r.attempt.passed)
          throw new InputError(
            `${attemptText(r.attempt)}: no alcanza la nota mínima (${r.program.exam.passMark}). Quedan ${r.attemptsLeft} intentos.`,
          );
        return `Aprobado con ${attemptText(r.attempt)}; constancia ${r.certificate?.id ?? ''} emitida.`;
      });
    },
    sibDeclare: (t, d) => {
      if (!d.sibDeclare) return;
      const id = d.sibDeclare;
      const number = document
        .querySelector<HTMLInputElement>(`[data-sib-number="${CSS.escape(id)}"]`)
        ?.value.trim();
      void act(t, 'Registrando la credencial…', async () => {
        if (!number) throw new InputError('Escribe el número de la credencial SIB.');
        await api('PUT', `/v1/onboardings/${encodeURIComponent(id)}/sib-credential`, { number });
        await render(current, { quiet: true });
        return 'Credencial registrada; adjunta su evidencia para revisarla.';
      });
    },
    sibValid: (t, d) => {
      if (!d.sibValid) return;
      const id = d.sibValid;
      void act(t, 'Validando la credencial…', async () => {
        const o = await api<OnboardingView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(id)}/sib-credential/review`,
          { valid: true },
        );
        await render(current, { quiet: true });
        return `Credencial válida: ${o.candidate.fullName} omite la capacitación y el trámite SIB y sigue el alta con ${carrierName(o.carrier)}.`;
      });
    },
    sibInvalid: (t, d) => {
      if (!d.sibInvalid) return;
      const id = d.sibInvalid;
      const reason = document
        .querySelector<HTMLInputElement>(`[data-sib-reason="${CSS.escape(id)}"]`)
        ?.value.trim();
      void act(t, 'Registrando la revisión…', async () => {
        if (!reason) throw new InputError('Escribe por qué la credencial no es válida.');
        const o = await api<OnboardingView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(id)}/sib-credential/review`,
          { valid: false, reason },
        );
        await render(current, { quiet: true });
        const program = o.steps.find((x) => x.step === 'TRAINING_PROGRAM');
        return program?.evidence.assignedBy === 'NUBI'
          ? `Credencial no válida; se asignó el programa «${program.evidence.program}».`
          : 'Credencial no válida; asígnale el programa de capacitación cuando apruebe la selección.';
      });
    },
    sibEvidence: (t, d) => {
      if (!d.sibEvidence) return;
      const id = d.sibEvidence;
      void act(t, 'Descargando la evidencia…', async () => {
        const name = await download(
          `/v1/onboardings/${encodeURIComponent(id)}/sib-credential/evidence`,
        );
        return `«${name}» descargado.`;
      });
    },
    psySave: (t) => {
      const url = ($('#psy-url') as HTMLInputElement).value.trim();
      void act(t, 'Guardando el enlace…', async () => {
        if (url && !/^https:\/\//i.test(url))
          throw new InputError('El enlace debe empezar con https://');
        await api('PUT', '/v1/onboarding-settings', { psychometricTestUrl: url || null });
        await render(current, { quiet: true });
        return url ? 'Enlace de la prueba psicométrica guardado.' : 'Enlace de la prueba quitado.';
      });
    },
    psyInvite: (t, d) => {
      if (!d.psyInvite) return;
      const channel = d.send === 'WHATSAPP' ? 'WHATSAPP' : 'EMAIL';
      void act(t, 'Enviando la prueba…', async () => {
        const o = await api<OnboardingView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(d.psyInvite ?? '')}/psychometric/invitation`,
          { channel },
        );
        await render(current, { quiet: true });
        return `Prueba psicométrica enviada a ${o.candidate.fullName} por ${channel === 'WHATSAPP' ? 'WhatsApp' : 'correo'}.`;
      });
    },
    psyDownload: (t, d) => {
      if (!d.psyDownload) return;
      void act(t, 'Descargando el informe…', async () => {
        const name = await download(
          `/v1/onboardings/${encodeURIComponent(d.psyDownload ?? '')}/psychometric/report`,
        );
        return `«${name}» descargado.`;
      });
    },
    psyRecord: (t, d) => {
      if (!d.psyRecord) return;
      const box = document.querySelector<HTMLElement>(
        `[data-psy-form="${CSS.escape(d.psyRecord)}"]`,
      );
      const value = (k: string) =>
        box
          ?.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-psy="${k}"]`)
          ?.value.trim() ?? '';
      const result = value('result');
      const note = value('note');
      void act(t, 'Registrando la evaluación…', async () => {
        if (!value('takenOn')) throw new InputError('Escribe la fecha en que se aplicó.');
        if (!value('appliedBy')) throw new InputError('Escribe quién aplicó la evaluación.');
        if (result === 'UNFIT' && !note) throw new InputError('Escribe el motivo de «No apta».');
        if (result === 'FIT_WITH_OBSERVATIONS' && !note)
          throw new InputError('Escribe las observaciones.');
        const o = await api<OnboardingView>(
          'POST',
          `/v1/onboardings/${encodeURIComponent(d.psyRecord ?? '')}/psychometric`,
          {
            result,
            takenOn: value('takenOn'),
            appliedBy: value('appliedBy'),
            ...(note ? { note } : {}),
          },
        );
        await render(current, { quiet: true });
        return o.status === 'REJECTED'
          ? `Onboarding de ${o.candidate.fullName} cerrado: no apta. Se le avisó por correo.`
          : `Evaluación psicométrica registrada: ${o.candidate.fullName} pasa a capacitación.`;
      });
    },
  },
  submit: {
    'onb-search-form': (ev) => {
      ev.preventDefault();
      searchOnboardings('onb-q');
    },
    'onb-form': (ev) => {
      ev.preventDefault();
      void act($('#onb-submit'), 'Registrando al candidato…', async () => {
        const productLines = checkedValues(document, '#onb-products wa-checkbox');
        if (!productLines.length) throw new InputError('Elige al menos una línea de producto.');
        const phone = ($('#onb-phone') as HTMLInputElement).value.trim();
        const sibCredential = ($('#onb-sib') as HTMLInputElement).value.trim();
        const o = await api<OnboardingView>('POST', '/v1/onboardings', {
          candidate: {
            fullName: textInput('#onb-name', 'el nombre'),
            email: textInput('#onb-email', 'el correo'),
            phone: phone || null,
            source: ($('#onb-source') as HTMLSelectElement).value,
          },
          carrier: ($('#onb-carrier') as HTMLSelectElement).value,
          productLines,
          ...(sibCredential ? { sibCredential } : {}),
        });
        await render(current, { quiet: true });
        return o.sibCredential
          ? `Onboarding de ${o.candidate.fullName} iniciado; adjunta la evidencia de su credencial SIB para revisarla.`
          : `Onboarding de ${o.candidate.fullName} iniciado.`;
      });
    },
  },
  change: {
    'onb-f-status': () => searchOnboardings('onb-f-status'),
    'onb-f-carrier': () => searchOnboardings('onb-f-carrier'),
    'onb-f-line': () => searchOnboardings('onb-f-line'),
    'onb-f-age': () => searchOnboardings('onb-f-age'),
    // Only the lines the chosen carrier sells.
    'onb-carrier': (sel) => {
      void api<(CarrierView & { productLines: string[] })[]>('GET', '/v1/carriers').then((cs) => {
        const c = cs.find((x) => x.id === sel.value);
        $('#onb-products').innerHTML = (c?.productLines ?? [])
          .map((l) => checkboxControl({ class: 'check', value: l }, esc(PRODUCT[l] ?? l)))
          .join('');
      });
    },
  },
  changeData: {
    // Saved as it is marked: when the time is up, what was answered is graded.
    examAnswer: (control) => {
      const id = control.dataset.examAnswer;
      const question = control.dataset.question;
      if (!id || !question) return;
      void api<{ graded: ExamAttemptView | null }>(
        'PUT',
        `/v1/enrollments/${encodeURIComponent(id)}/exam/answers`,
        { answers: { [question]: Number(control.value) } },
      ).then(
        async (r) => {
          if (!r.graded) return;
          await render(current, { quiet: true });
          toast(`Se acabó el tiempo: ${attemptText(r.graded)}.`);
        },
        (e) => toast('No se pudo guardar la respuesta; vuelve a marcarla.', 'error').fail(e),
      );
    },
    sibUpload: (control) => {
      const up = control as unknown as HTMLInputElement;
      const file = up.files?.[0];
      const id = up.dataset.sibUpload;
      if (!file || !id) return;
      const label = up.parentElement?.querySelector<HTMLElement>('label.file-btn') ?? null;
      void act(label, 'Subiendo la evidencia…', async () => {
        await sendFile(`/v1/onboardings/${encodeURIComponent(id)}/sib-credential/evidence`, file);
        await render(current, { quiet: true });
        return `«${file.name}» adjunto; la credencial queda por revisar.`;
      });
    },
    psyReport: (control) => {
      const up = control as unknown as HTMLInputElement;
      const file = up.files?.[0];
      if (!file || !up.dataset.psyReport) return;
      const label =
        up.closest('.order-buttons')?.querySelector<HTMLElement>('label.file-btn') ?? null;
      void act(label, 'Subiendo el informe…', async () => {
        if (file.type && file.type !== 'application/pdf')
          throw new InputError('El informe debe ser un PDF.');
        await sendFile<OnboardingView>(
          `/v1/onboardings/${encodeURIComponent(up.dataset.psyReport ?? '')}/psychometric/report`,
          file,
        );
        await render(current, { quiet: true });
        return `Informe «${file.name}» adjunto a la evaluación psicométrica.`;
      });
    },
  },
};
